// Audience Layering Coverage — Audiences section, LLM judgment row.
// Renders Claude's verdict on whether audience attachments are coherent across the account's
// Search campaigns. Supporting data: per-campaign audience-count summary + a coverage band
// showing how many campaigns have audiences attached.

import classNames from 'classnames';

import type { EvidenceProps } from './registry';

interface PerCampaignSummary {
  campaignId?: string;
  campaignName?: string;
  biddingStrategy?: string;
  audienceCount: number;
  userListCount: number;
  customAudienceCount: number;
  bidModifierRange: { min: number; max: number } | null;
}

interface AudienceLayeringShape {
  accountName: string;
  cid: string;
  totalSearchCampaigns: number;
  campaignsWithAudiences: number;
  campaignsWithoutAudiences: number;
  totalAudienceAttachments: number;
  userListAttachments: number;
  customAudienceAttachments: number;
  perCampaign: PerCampaignSummary[];
}

const STRATEGY_LABEL: Record<string, string> = {
  MANUAL_CPC: 'Manual CPC',
  ENHANCED_CPC: 'Enhanced CPC',
  MAXIMIZE_CONVERSIONS: 'Max Conversions',
  MAXIMIZE_CONVERSION_VALUE: 'Max Conv. Value',
  TARGET_CPA: 'Target CPA',
  TARGET_ROAS: 'Target ROAS',
  TARGET_IMPRESSION_SHARE: 'Target Imp. Share',
  TARGET_SPEND: 'Target Spend',
  PERCENT_CPC: 'Percent CPC'
};

function strategyLabel(s: string | undefined): string {
  if (!s) return '—';
  return STRATEGY_LABEL[s] ?? s;
}

function formatDelta(n: number): string {
  const pct = Math.round((n - 1) * 100);
  if (pct === 0) return '0%';
  return `${pct > 0 ? '+' : ''}${pct}%`;
}

export const AudienceLayeringCoverageEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as AudienceLayeringShape;

  const coveragePct =
    e.totalSearchCampaigns > 0
      ? Math.round((e.campaignsWithAudiences / e.totalSearchCampaigns) * 100)
      : 0;

  // Sort the per-campaign table: campaigns WITHOUT audiences first (most actionable), then
  // campaigns with most audiences first.
  const sorted = [...e.perCampaign].sort((a, b) => {
    if ((a.audienceCount === 0) !== (b.audienceCount === 0)) return a.audienceCount === 0 ? -1 : 1;
    return b.audienceCount - a.audienceCount;
  });

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Coverage at a glance
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalSearchCampaigns} Search campaigns
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{coveragePct}%</div>
            <div className='audience-coverage__lbl'>Campaigns with audiences</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>
              {e.campaignsWithoutAudiences.toLocaleString()}
            </div>
            <div className='audience-coverage__lbl'>Campaigns missing audiences</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.userListAttachments.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>User-list attachments</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>
              {e.customAudienceAttachments.toLocaleString()}
            </div>
            <div className='audience-coverage__lbl'>Custom-audience attachments</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Per-campaign audience attachments
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalSearchCampaigns} campaigns
          </span>
        </h4>
        <table className='evidence__table evidence__table--audience-layering'>
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Bidding strategy</th>
              <th className='evidence__th--right'>Audiences</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(c => {
              const missing = c.audienceCount === 0;
              return (
                <tr
                  key={c.campaignId ?? c.campaignName}
                  className={classNames({ 'evidence__row--flagged': missing })}
                >
                  <td>{c.campaignName ?? '(unnamed)'}</td>
                  <td>{strategyLabel(c.biddingStrategy)}</td>
                  <td className='evidence__td--right'>{c.audienceCount.toLocaleString()}</td>
                  <td>
                    {missing ? (
                      <span className='evidence__inline-note'>No audiences attached</span>
                    ) : (
                      <>
                        {c.userListCount > 0 && (
                          <span className='evidence__audience-chip'>
                            {c.userListCount} user-list
                          </span>
                        )}
                        {c.customAudienceCount > 0 && (
                          <span className='evidence__audience-chip'>
                            {c.customAudienceCount} custom
                          </span>
                        )}
                        {c.bidModifierRange && (
                          <span className='evidence__inline-note'>
                            bid mod {formatDelta(c.bidModifierRange.min)} to{' '}
                            {formatDelta(c.bidModifierRange.max)}
                          </span>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        Audience layering = user-list / custom-audience criteria attached to a Search campaign.
        Under Smart Bidding (Target CPA, Target ROAS, Max Conversions, Max Conv. Value), observation
        audiences are read as <strong>signals</strong> rather than bid multipliers. Coverage on
        non-brand campaigns is the highest-leverage gap to close.
      </p>
    </div>
  );
};
